import * as React from 'react'
import { MapPin, Plus, X } from 'lucide-react'
import {
  equipmentBoxId,
  equipmentTimerId,
  problemEquipmentId,
  problemTimerId,
  problemWaterSourceId,
  problemZoneId,
} from '@lib/dto/irrigation.dto'
import type {
  IrrigationEquipmentDto,
  IrrigationPartSummaryDto,
  IrrigationProblemDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  WaterSourceDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import type { IrrigationPartDto } from '@lib/dto/housecall-pro.dto'
import {
  BACKFLOW_TYPES,
  BACKFLOW_TYPE_LABELS,
  HEAD_TYPES,
  HEAD_TYPE_LABELS,
  IRRIGATION_EQUIPMENT_KIND_LABELS,
  IRRIGATION_PROBLEM_SUBJECT_LABELS,
  LINE_SIZES,
  METER_SIZES,
  PART_KIND_FOR_EQUIPMENT,
  PIPE_SIZE_LABELS,
  SENSOR_TYPES,
  SENSOR_TYPE_LABELS,
  VALVE_SIZES,
  WATER_SOURCE_KINDS,
  WATER_SOURCE_KIND_LABELS,
  WELL_COMPONENT_TYPES,
  WELL_COMPONENT_TYPE_LABELS,
  isBoxableKind,
  type IrrigationEquipmentKind,
} from '@lib/enums/irrigation.enum'
import { distanceMeters } from '@lib/helpers/geo'
import { calendarDateSchema } from '@lib/validators/calendar-date.validator'
import type { IrrigationPartKind } from '@lib/enums/irrigation-part-kind.enum'
import { PROBLEM_SEVERITIES } from '@lib/enums/problem-severity.enum'
import {
  MAX_SPLICE_COUNT,
  type IrrigationEntity,
} from '@lib/input/irrigation.input'
import type { CommitResult } from '@/hooks/useIrrigationSurvey'
import {
  operationFingerprint,
  type IrrigationChange,
} from '@/irrigation-survey/irrigationOperations'
import { PartPicker } from '@/components/PartPicker'
import { SurveyPhotos } from '@/components/SurveyPhotos'
import { SurveyVideos } from '@/components/SurveyVideos'
import { ProblemExplainer } from '@/components/ProblemExplainer'
import { useVideoAvailability } from '@/hooks/useVideos'
import type { VideoOwner } from '@lib/input/tree-video.input'
import { Button } from '@/components/ui/button'
import { ChangeAnswers } from '@/site-visits/ChangeAnswers'
import {
  addQuestion,
  removeQuestion,
  type ChangeQuestion,
} from '@/site-visits/siteRecordChanges'
import { TRACKED, editAsks } from '@/irrigation-survey/changeQuestions'
import {
  LastVisitProblems,
  type IrrigationLastVisit,
} from '@/irrigation-survey/LastVisitProblems'
import { bringIrrigationProblemForward } from '@/site-visits/lastVisit'
import { RecordHistory } from '@/site-visits/RecordHistory'
import { cn } from '@/lib/utils'
import {
  Chips,
  LinkRow,
  Section,
  SelectField,
  Stepper,
  TextField,
} from '@/irrigation-survey/fields'
import {
  NextStep,
  SystemChecklist,
  ZoneCountField,
} from '@/irrigation-survey/SystemChecklist'
import {
  equipmentStepId,
  sensorStepId,
  zoneStepId,
  type StepAnchor,
} from '@/irrigation-survey/systemSteps'
import {
  equipmentName,
  problemSubject,
  problemTitle,
  timerName,
  waterSourceName,
  zoneName,
  zonesInOrder,
} from '@/irrigation-survey/irrigationLabels'
import {
  newProblem,
  newZone,
  withBox,
  withUsualRepair,
  withWaterSourceKind,
  type ProblemSubjectRef,
} from '@/irrigation-survey/irrigationDrafts'
import { ValveBoxContents } from '@/irrigation-survey/ValveBoxContents'

/**
 * What a sheet shows: one record, and whether it exists yet; or a timer's
 * zones, which are recorded as a step of their own rather than inside the
 * timer's sheet.
 */
export type SheetTarget =
  | { entity: 'waterSource'; record: WaterSourceDto; isNew: boolean }
  | { entity: 'timer'; record: IrrigationTimerDto; isNew: boolean }
  | { entity: 'zones'; record: IrrigationTimerDto; isNew: false }
  | {
      entity: 'zone'
      record: ZoneDto
      isNew: boolean
      /** Opened from one of last visit's faint pins: show what it found. */
      showLastVisit?: boolean
    }
  | { entity: 'equipment'; record: IrrigationEquipmentDto; isNew: boolean }
  | { entity: 'problem'; record: IrrigationProblemDto; isNew: boolean }

/**
 * What can be dropped on the map. Zones are added from their timer and well
 * components from their well; the rest of the system is placed from its
 * steps, which say what it is on.
 */
export type PlaceKind =
  | 'waterSource'
  | 'timer'
  | 'problem'
  | Exclude<IrrigationEquipmentKind, 'WELL_COMPONENT'>

/** Something to drop on the map, and the source or timer it belongs to. */
export interface PlaceRequest {
  kind: PlaceKind
  waterSourceId?: string
  timerId?: string
  /**
   * The zone a problem at a spot goes into, when a zone's own sheet asked for
   * it. Left out, it goes into the zone being walked.
   */
  zoneId?: string
}

/** What an editor can do beyond its own fields. */
export interface EditorContext {
  site: IrrigationSiteDto
  /**
   * Whether problems can be recorded: they belong to a visit, and there must
   * be one being worked in (see `canRecordIn`). The rest of the system is
   * the site's and can always be changed.
   */
  canRecordFindings: boolean
  /** Problems could be recorded, but this visit's have not loaded yet. */
  findingsLoading?: boolean
  problemTypes: readonly IrrigationProblemTypeDto[]
  pendingKeys: ReadonlySet<string>
  /** Photos upload only online; see SurveyPhotos. */
  isOnline: boolean
  /**
   * Whether a change to the system is made on a revisit, and so asks "Was it
   * wrong, or did it change?" on its Save (docs/site-visits.md).
   */
  isRevisit?: boolean
  /**
   * The visit before this one, on a revisit: what it found, for each sheet's
   * problems to show and bring forward with Still here. Null on a first
   * visit, or while there is no visit to bring anything into.
   */
  lastVisit?: IrrigationLastVisit | null
  commit: (
    change: IrrigationChange,
    isCorrection?: boolean,
    changedOn?: string,
  ) => CommitResult
  /**
   * Whether a change that asks is made in no visit, and so also asks when
   * it happened (docs/site-visits.md).
   */
  asksWhen?: boolean
  /**
   * Whether the system itself can be changed here. False while an earlier
   * visit is shown: the site record is only ever changed as it stands now,
   * so only that visit's problems can be (docs/site-visits.md).
   */
  canEditRecord?: boolean
  /** Show another record's sheet. */
  open: (target: SheetTarget) => void
  close: () => void
  /** Hand the screen to the map to drop a new record at the crosshair. */
  place: (request: PlaceRequest) => void
  currentZoneId: string | null
  setCurrentZoneId: (zoneId: string | null) => void
  /** Start dragging the shown record's pin; null when it has none of its own. */
  move: (() => void) | null
  /** Start drawing the shown zone's outline; null for anything but a saved zone. */
  outline: (() => void) | null
}

/** A sheet's own fields: a bare fieldset, dimmed when it is locked. */
const RECORD = 'm-0 min-w-0 space-y-4 border-0 p-0 disabled:opacity-60'

/** A footer action that hands the screen to the map. */
interface MapAction {
  label: string
  run: () => void
}

function moveAction(context: EditorContext): MapAction | null {
  return context.move === null ? null : { label: 'Move pin', run: context.move }
}

function toSummary(part: IrrigationPartDto): IrrigationPartSummaryDto {
  return {
    uuid: part.uuid,
    name: part.name,
    partNumber: part.partNumber,
    gone: false,
  }
}

/* -------------------------------------------------------------------------- */
/* Shared pieces                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The draft a sheet edits, and saving it. A new record is committed on the
 * first Save; an existing one on every Save after that. A refusal leaves the
 * draft as it was, with the reason beside the button.
 */
/**
 * Fields a sheet may patch in place. `kind` is left out: a water source or a
 * piece of equipment is a variant, and a patch must not switch it while
 * keeping the other variant's fields.
 */
type DraftPatch<T> = Partial<Omit<T, 'kind'>>

function useRecordDraft<T extends { id: string; updatedAt: string }>(
  entity: Exclude<IrrigationEntity, never>,
  record: T,
  isNew: boolean,
  context: EditorContext,
  toChange: (record: T) => IrrigationChange,
) {
  const [draft, setDraft] = React.useState<T>(record)
  const [error, setError] = React.useState<string | null>(null)
  const [savedAt, setSavedAt] = React.useState<number | null>(null)
  const [confirmingRemove, setConfirmingRemove] = React.useState(false)
  // Compared as what would be sent, not as records: the server fills in
  // fields of its own (a timer's area, a part leaving the price book), and
  // those arriving must not make an untouched sheet look edited.
  const dirty =
    isNew ||
    operationFingerprint(toChange(draft)) !==
      operationFingerprint(toChange(record))
  const tracked = context.isRevisit === true ? TRACKED[entity] : null
  /** What Save asks on a revisit; null when it saves without asking. */
  function saveAsks(): ChangeQuestion | null {
    if (tracked === null) return null
    if (isNew) return addQuestion(tracked)
    return editAsks(toChange(record), toChange(draft))
  }

  function update(patch: DraftPatch<T>): void {
    setDraft({ ...draft, ...patch })
    setError(null)
    setSavedAt(null)
  }

  /** Replace the draft. A kind switch builds a whole variant; a patch cannot. */
  function replace(next: T): void {
    setDraft(next)
    setError(null)
    setSavedAt(null)
  }

  /**
   * `isCorrection` is the answer to the question Save asked, if it asked
   * one, and `changedOn` the day it changed, if that was asked too.
   */
  function save(isCorrection?: boolean, changedOn?: string): boolean {
    const next = { ...draft, updatedAt: new Date().toISOString() }
    const result = context.commit(toChange(next), isCorrection, changedOn)
    if (!result.ok) {
      setError(result.message)
      return false
    }
    setError(null)
    setSavedAt(Date.now())
    return true
  }

  /** `isCorrection`: true for recorded by mistake, false for gone. */
  function remove(isCorrection?: boolean, changedOn?: string): void {
    const result = context.commit(
      { kind: 'archive', entity, id: record.id },
      isCorrection,
      changedOn,
    )
    if (!result.ok) {
      setError(result.message)
      setConfirmingRemove(false)
      return
    }
    context.close()
  }

  return {
    draft,
    update,
    replace,
    dirty,
    error,
    savedAt,
    save,
    saveQuestion: dirty ? saveAsks() : null,
    remove,
    removeQuestion: tracked === null ? null : removeQuestion(tracked),
    confirmingRemove,
    setConfirmingRemove,
  }
}

/**
 * A saved record's photos and videos. Not offered on a record that has never
 * been saved: the first Save reopens the sheet as the saved record, and its
 * media can be added once that save has reached the server, which online
 * takes about a second.
 *
 * Video only where the deployment has Stream, as on the tree sheet: an
 * optional capability that is switched off should look absent, not broken.
 */
function RecordMedia({
  owner,
  pendingKey,
  context,
  label,
}: {
  owner: VideoOwner
  /** The record's key in the sync queue, `entity:id`. */
  pendingKey: string
  context: EditorContext
  label?: string
}) {
  const videoAvailability = useVideoAvailability()
  const isPendingSync = context.pendingKeys.has(pendingKey)
  return (
    <>
      <SurveyPhotos
        scope={owner}
        isOnline={context.isOnline}
        isPendingSync={isPendingSync}
        controlClassName="h-11 lg:h-9"
        label={label}
      />
      {videoAvailability.data === true ? (
        <SurveyVideos
          owner={owner}
          isOnline={context.isOnline}
          isPendingSync={isPendingSync}
          controlClassName="h-11 lg:h-9"
        />
      ) : null}
    </>
  )
}

interface FooterProps {
  isNew: boolean
  dirty: boolean
  error: string | null
  saved: boolean
  /** With the answer, when `question` asked one, and the day if asked. */
  onSave: (isCorrection?: boolean, changedOn?: string) => void
  /** What saving asks on a revisit, in place of Save; null asks nothing. */
  question?: ChangeQuestion | null
  /** Ask when it happened too: for a change made in no visit. */
  askWhen?: boolean
  /**
   * The record cannot be changed here: an earlier visit is shown. In place
   * of the footer, a line saying where it is changed.
   */
  locked?: boolean
  onCancel: () => void
  /**
   * Move pin, or draw a zone's outline. Null for a record with neither.
   */
  mapAction: MapAction | null
  /** Omitted for a record that cannot be removed from here. */
  remove?: {
    confirming: boolean
    setConfirming: (confirming: boolean) => void
    onRemove: (isCorrection?: boolean, changedOn?: string) => void
    noun: string
    /** What removing asks on a revisit, in place of Remove. */
    question?: ChangeQuestion | null
  }
}

/** Save, and Remove behind a second tap: easy to do on purpose, hard by accident. */
function EditorFooter({
  isNew,
  dirty,
  error,
  saved,
  onSave,
  question = null,
  askWhen = false,
  locked = false,
  onCancel,
  mapAction,
  remove,
}: FooterProps) {
  if (locked) {
    return (
      <p className="border-t border-default pt-3 text-xs text-secondary-text">
        Shown as it was on this visit. The system itself is changed on the
        newest visit.
      </p>
    )
  }
  return (
    <div className="space-y-2 border-t border-default pt-3">
      {error === null ? null : (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {question === null ? (
          <Button
            type="button"
            disabled={!dirty}
            onClick={() => {
              onSave()
            }}
          >
            {isNew ? 'Save' : 'Save changes'}
          </Button>
        ) : (
          <ChangeAnswers
            question={question}
            askWhen={askWhen}
            onAnswer={onSave}
          />
        )}
        {isNew ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        {/*
          Offered only once the sheet is saved. Moving a pin or drawing an
          outline hands the screen to the map, and an unsaved edit would be
          lost on the way; afterwards the sheet reopens from the record.
        */}
        {mapAction === null || isNew || dirty ? null : (
          <Button type="button" variant="ghost" onClick={mapAction.run}>
            {mapAction.label}
          </Button>
        )}
        {saved && !dirty ? (
          <span className="text-sm text-muted-text">Saved on this phone</span>
        ) : null}
        {remove === undefined || isNew ? null : remove.confirming ? (
          <span className="ml-auto flex items-center gap-1">
            <span className="text-sm text-secondary-text">
              Remove this {remove.noun}?
            </span>
            {remove.question == null ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  remove.onRemove()
                }}
              >
                Remove
              </Button>
            ) : (
              <ChangeAnswers
                question={remove.question}
                tone="remove"
                size="sm"
                askWhen={askWhen}
                onAnswer={remove.onRemove}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                remove.setConfirming(false)
              }}
            >
              Keep
            </Button>
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-secondary-text"
            onClick={() => {
              remove.setConfirming(true)
            }}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  )
}

/** The installed model: shown, changed through the price book, or cleared. */
function PartField({
  label,
  part,
  kind,
  onChange,
}: {
  label: string
  part: IrrigationPartSummaryDto | null
  kind: IrrigationPartKind | null
  onChange: (part: IrrigationPartSummaryDto | null) => void
}) {
  const [picking, setPicking] = React.useState(false)
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-secondary-text">{label}</p>
      {picking ? (
        <div className="space-y-1">
          <PartPicker
            autoFocus
            kind={kind ?? undefined}
            onPick={(picked) => {
              onChange(toSummary(picked))
              setPicking(false)
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setPicking(false)
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              part === null ? 'text-muted-text' : null,
            )}
          >
            {part === null ? 'Not identified' : part.name}
            {part?.gone === true ? (
              <span className="ml-1 text-xs text-warning">
                no longer in the price book
              </span>
            ) : null}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setPicking(true)
            }}
          >
            {part === null ? 'Identify' : 'Change'}
          </Button>
          {part === null ? null : (
            <button
              type="button"
              className="rounded p-1 text-muted-text hover:bg-subtle"
              aria-label={`Clear ${label.toLowerCase()}`}
              onClick={() => {
                onChange(null)
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Whether a problem is on the thing a sheet is for. */
function isOn(
  problem: IrrigationProblemDto,
  subject: ProblemSubjectRef,
): boolean {
  switch (subject.kind) {
    case 'zone':
      return problemZoneId(problem) === subject.zoneId
    case 'timer':
      return problemTimerId(problem) === subject.timerId
    case 'equipment':
      return problemEquipmentId(problem) === subject.equipmentId
    case 'waterSource':
      return problemWaterSourceId(problem) === subject.waterSourceId
  }
}

/** The open problems recorded on one thing, and a way to add another. */
function ProblemsOn({
  subject,
  problems,
  showLastVisit = false,
  context,
}: {
  subject: ProblemSubjectRef
  problems: readonly IrrigationProblemDto[]
  /** Open on what last visit found, and bring it into view. */
  showLastVisit?: boolean
  context: EditorContext
}) {
  // A zone's problem is either at a spot, pinned on the map, or on the zone
  // as a whole. They are different kinds of problem, so ask which first.
  const [choosing, setChoosing] = React.useState(false)
  const open = problems.filter((problem) => problem.fixedAt === null)
  function addOnWhole(): void {
    context.open({
      entity: 'problem',
      record: newProblem(context.site.siteId, subject),
      isNew: true,
    })
  }
  return (
    <Section
      title="Problems"
      action={
        choosing ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setChoosing(false)
            }}
          >
            Cancel
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!context.canRecordFindings}
            onClick={() => {
              if (subject.kind === 'zone') setChoosing(true)
              else addOnWhole()
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add problem
          </Button>
        )
      }
    >
      {choosing && subject.kind === 'zone' ? (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <Button
              type="button"
              variant="outline"
              className="h-11 justify-start lg:h-9"
              onClick={() => {
                context.place({ kind: 'problem', zoneId: subject.zoneId })
              }}
            >
              <MapPin className="h-4 w-4" />
              Problem at a spot
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 justify-start lg:h-9"
              onClick={addOnWhole}
            >
              Zone-wide problem
            </Button>
          </div>
          <p className="text-xs text-muted-text">
            A spot is pinned on the map, like a broken riser. Zone-wide is the
            whole zone, like a weeping valve.
          </p>
        </div>
      ) : null}
      {context.canRecordFindings ? null : (
        <p className="text-xs text-muted-text">
          {context.findingsLoading === true
            ? "Loading this visit's problems."
            : 'Problems belong to a visit. Start one above to record them.'}
        </p>
      )}
      {open.length === 0 ? (
        <p className="text-sm text-muted-text">None recorded.</p>
      ) : (
        <ul className="divide-y divide-default rounded-md border border-default">
          {open.map((problem) => (
            <LinkRow
              key={problem.id}
              tone="problem"
              title={problemTitle(problem, context.problemTypes)}
              detail={
                problem.severity === null
                  ? 'Not rated'
                  : `Severity ${String(problem.severity)}`
              }
              pending={context.pendingKeys.has(`problem:${problem.id}`)}
              onClick={() => {
                context.open({
                  entity: 'problem',
                  record: problem,
                  isNew: false,
                })
              }}
            />
          ))}
        </ul>
      )}
      {context.lastVisit === null ||
      context.lastVisit === undefined ||
      !context.canRecordFindings ? null : (
        <LastVisitProblems
          // Opened again, not left as it was, when a faint pin asks for it.
          key={showLastVisit ? 'asked' : 'closed'}
          initiallyOpen={showLastVisit}
          lastVisit={context.lastVisit}
          problems={(context.lastVisit.status === 'ready'
            ? context.lastVisit.problems
            : []
          ).filter((problem) => isOn(problem, subject))}
          current={context.site.problems}
          problemTypes={context.problemTypes}
          onBring={(problem) =>
            context.commit({
              kind: 'upsert',
              entity: 'problem',
              record: bringIrrigationProblemForward(problem),
            })
          }
        />
      )}
    </Section>
  )
}

function options<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
) {
  return values.map((value) => ({ value, label: labels[value] }))
}

/* -------------------------------------------------------------------------- */
/* Water source                                                               */
/* -------------------------------------------------------------------------- */

function WaterSourceEditor({
  record,
  isNew,
  context,
}: {
  record: WaterSourceDto
  isNew: boolean
  context: EditorContext
}) {
  const form = useRecordDraft(
    'waterSource',
    record,
    isNew,
    context,
    // "None here" is answered on the steps below, not in this form, so a
    // Save sends the record's answers as they stand rather than the ones it
    // had when the sheet opened.
    (next) => ({
      kind: 'upsert',
      entity: 'waterSource',
      record: { ...next, absentEquipment: record.absentEquipment },
    }),
  )
  const { draft } = form
  return (
    <div className="space-y-4">
      {/* What this is: the site's, changed only as it stands now. */}
      <fieldset disabled={context.canEditRecord === false} className={RECORD}>
        <Chips
          label="What it is"
          value={draft.kind}
          options={options(WATER_SOURCE_KINDS, WATER_SOURCE_KIND_LABELS)}
          onChange={(kind) => {
            form.replace(withWaterSourceKind(draft, kind))
          }}
        />
        <TextField
          label="Name (optional)"
          id="water-source-label"
          value={draft.label}
          placeholder="e.g. Meter by the gate"
          onChange={(label) => {
            form.update({ label })
          }}
        />
        {draft.kind === 'CITY_METER' ? (
          <SelectField
            label="Meter size"
            id="water-source-meter-size"
            value={draft.meterSize}
            options={options(METER_SIZES, PIPE_SIZE_LABELS)}
            emptyLabel="Not recorded"
            onChange={(meterSize) => {
              form.replace({ ...draft, meterSize })
            }}
          />
        ) : (
          <TextField
            label="Pump horsepower"
            id="water-source-horsepower"
            value={draft.horsepower}
            inputMode="decimal"
            onChange={(horsepower) => {
              form.replace({ ...draft, horsepower })
            }}
          />
        )}
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Static pressure (psi)"
            id="water-source-psi"
            value={draft.staticPsi === null ? null : String(draft.staticPsi)}
            inputMode="numeric"
            hint="With nothing running."
            onChange={(value) => {
              form.update({
                staticPsi:
                  value === null || Number.isNaN(Number(value))
                    ? null
                    : Math.round(Number(value)),
              })
            }}
          />
          <TextField
            label="Available flow (gpm)"
            id="water-source-gpm"
            value={draft.availableGpm}
            inputMode="decimal"
            onChange={(availableGpm) => {
              form.update({ availableGpm })
            }}
          />
        </div>
        <TextField
          label="Notes"
          id="water-source-notes"
          value={draft.notes}
          multiline
          onChange={(notes) => {
            form.update({ notes })
          }}
        />
      </fieldset>
      {isNew ? null : (
        <RecordMedia
          owner={{ waterSourceId: record.id }}
          pendingKey={`waterSource:${record.id}`}
          context={context}
        />
      )}
      {isNew ? null : (
        <RecordHistory
          siteId={context.site.siteId}
          entity="WATER_SOURCE"
          recordId={record.id}
        />
      )}
      <EditorFooter
        locked={context.canEditRecord === false}
        askWhen={context.asksWhen === true}
        isNew={isNew}
        dirty={form.dirty}
        error={form.error}
        saved={form.savedAt !== null}
        onSave={(isCorrection, changedOn) => {
          if (form.save(isCorrection, changedOn) && isNew)
            context.open({
              entity: 'waterSource',
              record: form.draft,
              isNew: false,
            })
        }}
        question={form.saveQuestion}
        onCancel={context.close}
        mapAction={moveAction(context)}
        remove={{
          confirming: form.confirmingRemove,
          setConfirming: form.setConfirmingRemove,
          onRemove: form.remove,
          question: form.removeQuestion,
          noun: 'water source',
        }}
      />
      {isNew ? null : (
        <>
          <SystemChecklist source={record} context={context} />
          <ProblemsOn
            subject={{ kind: 'waterSource', waterSourceId: record.id }}
            problems={context.site.problems.filter(
              (problem) => problemWaterSourceId(problem) === record.id,
            )}
            context={context}
          />
        </>
      )}
    </div>
  )
}

function EquipmentList({
  items,
  context,
  empty,
}: {
  items: readonly IrrigationEquipmentDto[]
  context: EditorContext
  empty: string
}) {
  if (items.length === 0)
    return <p className="text-sm text-muted-text">{empty}</p>
  return (
    <ul className="divide-y divide-default rounded-md border border-default">
      {items.map((item) => (
        <LinkRow
          key={item.id}
          title={
            item.kind === 'WELL_COMPONENT' && item.wellComponentType !== null
              ? WELL_COMPONENT_TYPE_LABELS[item.wellComponentType]
              : equipmentName(item, context.site)
          }
          detail={item.part?.name}
          pending={context.pendingKeys.has(`equipment:${item.id}`)}
          onClick={() => {
            context.open({ entity: 'equipment', record: item, isNew: false })
          }}
        />
      ))}
    </ul>
  )
}

/* -------------------------------------------------------------------------- */
/* Timer and its zones                                                        */
/* -------------------------------------------------------------------------- */

function headSummary(zone: ZoneDto): string {
  const counted = zone.headCounts.filter((entry) => entry.count > 0)
  if (counted.length === 0) return 'Heads not counted'
  return counted
    .map(
      (entry) =>
        `${String(entry.count)} ${HEAD_TYPE_LABELS[entry.type].toLowerCase()}`,
    )
    .join(', ')
}

/** Where a record's sheet stands in its source's steps, for "Next". */
function equipmentAnchor(
  record: IrrigationEquipmentDto,
  site: IrrigationSiteDto,
): StepAnchor | null {
  switch (record.kind) {
    case 'WELL_COMPONENT':
    case 'BALL_VALVE':
    case 'BACKFLOW':
    case 'MASTER_VALVE':
      return record.waterSourceId === null
        ? null
        : {
            kind: 'after',
            sourceId: record.waterSourceId,
            stepId: equipmentStepId(record.kind, record.waterSourceId),
          }
    case 'SENSOR': {
      const timer = site.timers.find((item) => item.id === record.timerId)
      return timer === undefined || timer.waterSourceId === null
        ? null
        : {
            kind: 'after',
            sourceId: timer.waterSourceId,
            stepId: sensorStepId(timer.id),
          }
    }
    case 'VALVE':
    case 'VALVE_BOX':
    case 'OTHER':
      return null
  }
}

/**
 * What the timer's name field says under it. On a site with more than one
 * timer it shows how this one's zones will read with the name as typed, since
 * that is the name a client sees on a bid ("Bungalows 4"), and it says when
 * another timer already has the name.
 */
function timerNameHint(
  timerId: string,
  label: string | null,
  site: IrrigationSiteDto,
): { text: string; warning: string | null } {
  const others = site.timers.filter((timer) => timer.id !== timerId)
  if (others.length === 0) {
    return { text: 'What the crew calls it.', warning: null }
  }
  const self = site.timers.find((timer) => timer.id === timerId)
  const timers = [
    ...others,
    { id: timerId, label, areaName: self?.areaName ?? null },
  ]
  const number =
    site.zones
      .filter((zone) => zone.timerId === timerId)
      .reduce<number | null>(
        (lowest, zone) =>
          lowest === null || zone.number < lowest ? zone.number : lowest,
        null,
      ) ?? 1
  const example = zoneName({ number, timerId }, timers)
  const text =
    label === null
      ? `Its zones read “${example}” until it has a name.`
      : `Its zones read “${example}”.`
  const trimmed = label?.trim().toLowerCase() ?? ''
  const taken =
    trimmed !== '' &&
    others.some((timer) => timer.label?.trim().toLowerCase() === trimmed)
  return {
    text,
    warning: taken
      ? 'Another timer on this site has that name, so their zones would read the same.'
      : null,
  }
}

function TimerEditor({
  record,
  isNew,
  context,
}: {
  record: IrrigationTimerDto
  isNew: boolean
  context: EditorContext
}) {
  const form = useRecordDraft('timer', record, isNew, context, (next) => ({
    kind: 'upsert',
    entity: 'timer',
    // "None here" for a sensor is answered on the steps, as on a source.
    record: { ...next, absentEquipment: record.absentEquipment },
  }))
  const { draft } = form
  const zoneCount = context.site.zones.filter(
    (zone) => zone.timerId === record.id,
  ).length
  const nameHint = timerNameHint(record.id, draft.label, context.site)
  const wired = context.site.equipment.filter(
    (item) => equipmentTimerId(item) === record.id,
  )
  // A master valve recorded on this timer's source before the timer was:
  // which timer it is wired to is a question nobody could answer then.
  const unwiredMasterValves = context.site.equipment.filter(
    (
      item,
    ): item is Extract<IrrigationEquipmentDto, { kind: 'MASTER_VALVE' }> =>
      item.kind === 'MASTER_VALVE' &&
      item.timerId === null &&
      item.waterSourceId !== null &&
      item.waterSourceId === record.waterSourceId,
  )
  const sourceOptions = context.site.waterSources.map((source) => ({
    value: source.id,
    label: waterSourceName(source.kind, source.label),
  }))
  const [wiringError, setWiringError] = React.useState<string | null>(null)
  return (
    <div className="space-y-4">
      {/* What this is: the site's, changed only as it stands now. */}
      <fieldset disabled={context.canEditRecord === false} className={RECORD}>
        {record.areaName === null ? null : (
          <p className="text-sm text-secondary-text">In {record.areaName}</p>
        )}
        <TextField
          label="Name"
          id="timer-label"
          value={draft.label}
          placeholder="e.g. Bungalows, Founders, A"
          hint={nameHint.text}
          onChange={(label) => {
            form.update({ label })
          }}
        />
        {nameHint.warning === null ? null : (
          <p className="-mt-3 text-xs text-warning">{nameHint.warning}</p>
        )}
        <SelectField
          label="Water source"
          id="timer-source"
          value={draft.waterSourceId}
          options={sourceOptions}
          emptyLabel="Not known yet"
          onChange={(waterSourceId) => {
            form.update({ waterSourceId })
          }}
        />
        <PartField
          label="Model"
          part={draft.part}
          kind="TIMER"
          onChange={(part) => {
            form.update({ part })
          }}
        />
        <TextField
          label="Notes"
          id="timer-notes"
          value={draft.notes}
          multiline
          onChange={(notes) => {
            form.update({ notes })
          }}
        />
      </fieldset>
      {isNew ? null : (
        <RecordMedia
          owner={{ irrigationTimerId: record.id }}
          pendingKey={`timer:${record.id}`}
          context={context}
        />
      )}
      {isNew ? null : (
        <RecordHistory
          siteId={context.site.siteId}
          entity="TIMER"
          recordId={record.id}
        />
      )}
      <EditorFooter
        locked={context.canEditRecord === false}
        askWhen={context.asksWhen === true}
        isNew={isNew}
        dirty={form.dirty}
        error={form.error}
        saved={form.savedAt !== null}
        onSave={(isCorrection, changedOn) => {
          if (form.save(isCorrection, changedOn) && isNew)
            context.open({ entity: 'timer', record: form.draft, isNew: false })
        }}
        question={form.saveQuestion}
        onCancel={context.close}
        mapAction={moveAction(context)}
        remove={{
          confirming: form.confirmingRemove,
          setConfirming: form.setConfirmingRemove,
          onRemove: form.remove,
          question: form.removeQuestion,
          noun: 'timer',
        }}
      />
      {isNew ? null : (
        <>
          <Section title="Zones">
            {context.canEditRecord === false ? null : (
              <ZoneCountField
                timer={record}
                context={context}
                onAdded={() => {
                  context.open({ entity: 'zones', record, isNew: false })
                }}
              />
            )}
            {zoneCount === 0 ? null : (
              <ul className="divide-y divide-default rounded-md border border-default">
                <LinkRow
                  title={`${String(zoneCount)} ${zoneCount === 1 ? 'zone' : 'zones'}`}
                  detail="Heads, valves and outlines, zone by zone"
                  onClick={() => {
                    context.open({ entity: 'zones', record, isNew: false })
                  }}
                />
              </ul>
            )}
          </Section>
          <Section title="Wired to it">
            <EquipmentList
              items={wired}
              context={context}
              empty="No master valve or sensor recorded on it yet."
            />
            {(context.canEditRecord === false ? [] : unwiredMasterValves).map(
              (valve) => (
                <div
                  key={valve.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-default px-3 py-2"
                >
                  <span className="text-sm">
                    The master valve on this water source is not wired to a
                    timer yet.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Wiring an unwired valve fills its record in, as a
                      // first outline does, so it asks nothing.
                      const result = context.commit(
                        {
                          kind: 'upsert',
                          entity: 'equipment',
                          record: {
                            ...valve,
                            timerId: record.id,
                            updatedAt: new Date().toISOString(),
                          },
                        },
                        true,
                      )
                      setWiringError(result.ok ? null : result.message)
                    }}
                  >
                    Wired to this one
                  </Button>
                </div>
              ),
            )}
            {wiringError === null ? null : (
              <p role="alert" className="text-sm text-danger">
                {wiringError}
              </p>
            )}
          </Section>
          {form.dirty || record.waterSourceId === null ? null : (
            <NextStep
              anchor={{
                kind: 'timer',
                sourceId: record.waterSourceId,
                timerId: record.id,
              }}
              context={context}
            />
          )}
          <ProblemsOn
            subject={{ kind: 'timer', timerId: record.id }}
            problems={context.site.problems.filter(
              (problem) => problemTimerId(problem) === record.id,
            )}
            context={context}
          />
        </>
      )}
    </div>
  )
}

/**
 * A timer's zones, as a step of their own: set up from the timer's zone
 * count, then filled in one by one. More can be added here when the count
 * was short.
 */
/** Where a zone's valve is, in its row on the timer's zone list. */
function valveWhere(valve: IrrigationEquipmentDto | undefined): string {
  if (valve === undefined) return 'no valve yet'
  return equipmentBoxId(valve) === null ? 'valve pinned' : 'valve in a box'
}

function ZoneListEditor({
  timer,
  context,
}: {
  timer: IrrigationTimerDto
  context: EditorContext
}) {
  const zones = zonesInOrder(context.site).filter(
    (zone) => zone.timerId === timer.id,
  )
  const anchor: StepAnchor | null =
    timer.waterSourceId === null
      ? null
      : {
          kind: 'after',
          sourceId: timer.waterSourceId,
          stepId: zoneStepId(timer.id),
        }
  function zoneDetail(zone: ZoneDto): string {
    const valve = context.site.equipment.find(
      (item) => item.kind === 'VALVE' && item.zoneId === zone.id,
    )
    const open = context.site.problems.filter(
      (problem) =>
        problemZoneId(problem) === zone.id && problem.fixedAt === null,
    ).length
    return [
      headSummary(zone),
      valveWhere(valve),
      ...(open === 0
        ? []
        : [`${String(open)} ${open === 1 ? 'problem' : 'problems'}`]),
      ...(context.currentZoneId === zone.id ? ['walking'] : []),
    ].join(' · ')
  }
  // Zones are the system's: an earlier visit lists them, and adds none.
  const locked = context.canEditRecord === false
  return (
    <div className="space-y-4">
      {locked ? null : <ZoneCountField timer={timer} context={context} />}
      <Section
        title="Zones"
        action={
          locked ? null : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                context.open({
                  entity: 'zone',
                  record: newZone(context.site, timer.id),
                  isNew: true,
                })
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add zone
            </Button>
          )
        }
      >
        {zones.length === 0 ? (
          <p className="text-sm text-muted-text">
            No zones yet. Enter how many the timer runs, above.
          </p>
        ) : (
          <ul className="divide-y divide-default rounded-md border border-default">
            {zones.map((zone) => (
              <LinkRow
                key={zone.id}
                title={zoneName(zone, context.site.timers)}
                detail={zoneDetail(zone)}
                pending={context.pendingKeys.has(`zone:${zone.id}`)}
                onClick={() => {
                  context.open({ entity: 'zone', record: zone, isNew: false })
                }}
              />
            ))}
          </ul>
        )}
      </Section>
      <NextStep anchor={anchor} context={context} />
    </div>
  )
}

function ZoneEditor({
  record,
  isNew,
  showLastVisit = false,
  context,
}: {
  record: ZoneDto
  isNew: boolean
  /** Open on what last visit found, for a sheet opened from its faint pin. */
  showLastVisit?: boolean
  context: EditorContext
}) {
  const form = useRecordDraft('zone', record, isNew, context, (next) => ({
    kind: 'upsert',
    entity: 'zone',
    record: next,
  }))
  const { draft } = form
  const foundValve = context.site.equipment.find(
    (item) => item.kind === 'VALVE' && item.zoneId === record.id,
  )
  const valve = foundValve?.kind === 'VALVE' ? foundValve : undefined
  const valveBox = context.site.equipment.find(
    (item) => valve !== undefined && item.id === valve.boxId,
  )
  const timer = context.site.timers.find((item) => item.id === record.timerId)
  const isCurrent = context.currentZoneId === record.id
  function countOf(type: (typeof HEAD_TYPES)[number]): number {
    return draft.headCounts.find((entry) => entry.type === type)?.count ?? 0
  }
  return (
    <div className="space-y-4">
      {/* What this is: the site's, changed only as it stands now. */}
      <fieldset disabled={context.canEditRecord === false} className={RECORD}>
        <TextField
          label="Number on the timer"
          id="zone-number"
          value={String(draft.number)}
          inputMode="numeric"
          onChange={(value) => {
            const number = Number(value)
            if (value !== null && Number.isInteger(number) && number > 0)
              form.update({ number })
          }}
        />
        <Section title="Heads">
          <div className="space-y-1">
            {HEAD_TYPES.map((type) => (
              <Stepper
                key={type}
                label={HEAD_TYPE_LABELS[type]}
                value={countOf(type)}
                onChange={(count) => {
                  const others = draft.headCounts.filter(
                    (entry) => entry.type !== type,
                  )
                  form.update({
                    headCounts:
                      count === 0 ? others : [...others, { type, count }],
                  })
                }}
              />
            ))}
          </div>
        </Section>
        <TextField
          label="Notes"
          id="zone-notes"
          value={draft.notes}
          multiline
          onChange={(notes) => {
            form.update({ notes })
          }}
        />
      </fieldset>
      {isNew ? null : (
        <RecordHistory
          siteId={context.site.siteId}
          entity="ZONE"
          recordId={record.id}
        />
      )}
      <EditorFooter
        locked={context.canEditRecord === false}
        askWhen={context.asksWhen === true}
        isNew={isNew}
        dirty={form.dirty}
        error={form.error}
        saved={form.savedAt !== null}
        onSave={(isCorrection, changedOn) => {
          if (form.save(isCorrection, changedOn) && isNew) {
            context.setCurrentZoneId(form.draft.id)
            context.open({ entity: 'zone', record: form.draft, isNew: false })
          }
        }}
        question={form.saveQuestion}
        onCancel={context.close}
        mapAction={
          context.outline === null
            ? null
            : {
                label:
                  record.boundary === null ? 'Draw outline' : 'Edit outline',
                run: context.outline,
              }
        }
        remove={{
          confirming: form.confirmingRemove,
          setConfirming: form.setConfirmingRemove,
          onRemove: form.remove,
          question: form.removeQuestion,
          noun: 'zone',
        }}
      />
      {isNew ? null : (
        <>
          <div className="flex items-center justify-between gap-2 rounded-md border border-default px-3 py-2">
            <span className="text-sm">
              {isCurrent
                ? 'This is the zone you are walking.'
                : 'Walking this zone? Problems you pin go into it.'}
            </span>
            {isCurrent ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  context.setCurrentZoneId(record.id)
                }}
              >
                Walk this zone
              </Button>
            )}
          </div>
          {timer === undefined ? null : (
            <Section title="Timer">
              <ul className="divide-y divide-default rounded-md border border-default">
                <LinkRow
                  title={timerName(timer, context.site.timers)}
                  detail={timer.part?.name}
                  onClick={() => {
                    context.open({
                      entity: 'timer',
                      record: timer,
                      isNew: false,
                    })
                  }}
                />
              </ul>
            </Section>
          )}
          <Section title="Valve">
            {valve === undefined ? (
              <p className="text-sm text-muted-text">
                No valve recorded for this zone. Drop a valve box, or a valve,
                from the map while this is the zone you are walking.
              </p>
            ) : (
              <ul className="divide-y divide-default rounded-md border border-default">
                <LinkRow
                  title={valve.part?.name ?? 'Valve'}
                  detail={
                    valve.size === null
                      ? undefined
                      : PIPE_SIZE_LABELS[valve.size]
                  }
                  pending={context.pendingKeys.has(`equipment:${valve.id}`)}
                  onClick={() => {
                    context.open({
                      entity: 'equipment',
                      record: valve,
                      isNew: false,
                    })
                  }}
                />
                {valveBox === undefined ? null : (
                  <LinkRow
                    title="In a valve box"
                    detail={equipmentName(valveBox, context.site)}
                    pending={context.pendingKeys.has(
                      `equipment:${valveBox.id}`,
                    )}
                    onClick={() => {
                      context.open({
                        entity: 'equipment',
                        record: valveBox,
                        isNew: false,
                      })
                    }}
                  />
                )}
              </ul>
            )}
          </Section>
          <ProblemsOn
            subject={{ kind: 'zone', zoneId: record.id, pin: null }}
            showLastVisit={showLastVisit}
            problems={context.site.problems.filter(
              (problem) => problemZoneId(problem) === record.id,
            )}
            context={context}
          />
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Equipment                                                                  */
/* -------------------------------------------------------------------------- */

function EquipmentEditor({
  record,
  isNew,
  context,
}: {
  record: IrrigationEquipmentDto
  isNew: boolean
  context: EditorContext
}) {
  const form = useRecordDraft('equipment', record, isNew, context, (next) => ({
    kind: 'upsert',
    entity: 'equipment',
    record: next,
  }))
  const { draft } = form
  const { site } = context
  const zoneOptions = zonesInOrder(site).map((zone) => ({
    value: zone.id,
    label: zoneName(zone, site.timers),
  }))
  const timerOptions = site.timers.map((timer) => ({
    value: timer.id,
    label: timerName(timer, site.timers),
  }))
  const sourceOptions = site.waterSources.map((source) => ({
    value: source.id,
    label: waterSourceName(source.kind, source.label),
  }))
  const kind: IrrigationEquipmentKind = draft.kind
  // The site's boxes, nearest first: on a campus there are dozens, and the
  // one this is in is almost always the one under it.
  const boxOptions = site.equipment
    .filter((item) => item.kind === 'VALVE_BOX')
    .map((box) => ({ box, meters: distanceMeters(box, draft) }))
    .sort((a, b) => a.meters - b.meters)
    .map(({ box, meters }) => ({
      value: box.id,
      label: `${equipmentName(box, site)} · ${String(Math.round(meters))} m`,
    }))

  function renderKindFields(): React.ReactNode {
    switch (draft.kind) {
      case 'VALVE':
        return (
          <>
            <SelectField
              label="Zone"
              id="equipment-zone"
              value={draft.zoneId}
              options={zoneOptions}
              emptyLabel="Not known yet"
              onChange={(zoneId) => {
                form.replace({ ...draft, zoneId })
              }}
            />
            <SelectField
              label="Size"
              id="equipment-size"
              value={draft.size}
              options={options(VALVE_SIZES, PIPE_SIZE_LABELS)}
              emptyLabel="Not recorded"
              onChange={(size) => {
                form.replace({ ...draft, size })
              }}
            />
            <PartField
              label="Decoder"
              part={draft.decoder}
              kind="DECODER"
              onChange={(decoder) => {
                form.replace({ ...draft, decoder })
              }}
            />
          </>
        )
      case 'MASTER_VALVE':
        return (
          <>
            <SelectField
              label="Wired to"
              id="equipment-timer"
              value={draft.timerId}
              options={timerOptions}
              emptyLabel="Not known yet"
              onChange={(timerId) => {
                form.replace({ ...draft, timerId })
              }}
            />
            <SelectField
              label="Water source"
              id="equipment-source"
              value={draft.waterSourceId}
              options={sourceOptions}
              emptyLabel="Not known yet"
              onChange={(waterSourceId) => {
                form.replace({ ...draft, waterSourceId })
              }}
            />
            <SelectField
              label="Size"
              id="equipment-size"
              value={draft.size}
              options={options(VALVE_SIZES, PIPE_SIZE_LABELS)}
              emptyLabel="Not recorded"
              onChange={(size) => {
                form.replace({ ...draft, size })
              }}
            />
            <PartField
              label="Decoder"
              part={draft.decoder}
              kind="DECODER"
              onChange={(decoder) => {
                form.replace({ ...draft, decoder })
              }}
            />
          </>
        )
      case 'BACKFLOW':
        return (
          <>
            <SelectField
              label="Water source"
              id="equipment-source"
              value={draft.waterSourceId}
              options={sourceOptions}
              emptyLabel="Not known yet"
              onChange={(waterSourceId) => {
                form.replace({ ...draft, waterSourceId })
              }}
            />
            <SelectField
              label="Type"
              id="equipment-backflow-type"
              value={draft.backflowType}
              options={options(BACKFLOW_TYPES, BACKFLOW_TYPE_LABELS)}
              emptyLabel="Not recorded"
              onChange={(backflowType) => {
                form.replace({ ...draft, backflowType })
              }}
            />
            <SelectField
              label="Size"
              id="equipment-size"
              value={draft.size}
              options={options(LINE_SIZES, PIPE_SIZE_LABELS)}
              emptyLabel="Not recorded"
              onChange={(size) => {
                form.replace({ ...draft, size })
              }}
            />
            <TextField
              label="Serial number"
              id="equipment-serial"
              value={draft.serialNumber}
              onChange={(serialNumber) => {
                form.replace({ ...draft, serialNumber })
              }}
            />
            <div className="space-y-1">
              <label
                htmlFor="equipment-tested"
                className="block text-xs font-medium text-secondary-text"
              >
                Last tested
              </label>
              <input
                id="equipment-tested"
                type="date"
                className="h-11 w-full rounded-md border border-default bg-surface px-2.5 text-sm lg:h-9"
                value={draft.lastTestedOn ?? ''}
                onChange={(event) => {
                  const value = event.target.value
                  if (value === '') {
                    form.replace({ ...draft, lastTestedOn: null })
                    return
                  }
                  const parsed = calendarDateSchema.safeParse(value)
                  if (!parsed.success) return
                  form.replace({ ...draft, lastTestedOn: parsed.data })
                }}
              />
            </div>
          </>
        )
      case 'BALL_VALVE':
        return (
          <>
            <SelectField
              label="Water source"
              id="equipment-source"
              value={draft.waterSourceId}
              options={sourceOptions}
              emptyLabel="Not known yet"
              onChange={(waterSourceId) => {
                form.replace({ ...draft, waterSourceId })
              }}
            />
            <SelectField
              label="Size"
              id="equipment-size"
              value={draft.size}
              options={options(LINE_SIZES, PIPE_SIZE_LABELS)}
              emptyLabel="Not recorded"
              onChange={(size) => {
                form.replace({ ...draft, size })
              }}
            />
          </>
        )
      case 'SENSOR':
        return (
          <>
            <SelectField
              label="Wired to"
              id="equipment-timer"
              value={draft.timerId}
              options={timerOptions}
              emptyLabel="Not known yet"
              onChange={(timerId) => {
                form.replace({ ...draft, timerId })
              }}
            />
            <SelectField
              label="Type"
              id="equipment-sensor-type"
              value={draft.sensorType}
              options={options(SENSOR_TYPES, SENSOR_TYPE_LABELS)}
              emptyLabel="Not recorded"
              onChange={(sensorType) => {
                form.replace({ ...draft, sensorType })
              }}
            />
          </>
        )
      case 'WELL_COMPONENT':
        return (
          <SelectField
            label="Component"
            id="equipment-well-type"
            value={draft.wellComponentType}
            options={options(WELL_COMPONENT_TYPES, WELL_COMPONENT_TYPE_LABELS)}
            onChange={(wellComponentType) => {
              form.replace({ ...draft, wellComponentType })
            }}
          />
        )
      case 'VALVE_BOX':
        return (
          <Stepper
            label="Wire splices"
            value={draft.spliceCount}
            max={MAX_SPLICE_COUNT}
            onChange={(spliceCount) => {
              form.replace({ ...draft, spliceCount })
            }}
          />
        )
      case 'OTHER':
        return (
          <TextField
            label="What it is"
            id="equipment-name"
            value={draft.name}
            placeholder="e.g. Quick coupler"
            onChange={(name) => {
              form.replace({ ...draft, name })
            }}
          />
        )
    }
  }

  return (
    <div className="space-y-4">
      {/* What this is: the site's, changed only as it stands now. */}
      <fieldset disabled={context.canEditRecord === false} className={RECORD}>
        {kind === 'OTHER' ? null : (
          <PartField
            label={kind === 'VALVE_BOX' ? 'Box' : 'Model'}
            part={draft.part}
            kind={PART_KIND_FOR_EQUIPMENT[kind]}
            onChange={(part) => {
              form.update({ part })
            }}
          />
        )}
        {renderKindFields()}
        {isBoxableKind(kind) && boxOptions.length > 0 ? (
          <SelectField
            label="Valve box"
            id="equipment-box"
            value={equipmentBoxId(draft)}
            options={boxOptions}
            emptyLabel="Not in a box"
            hint={
              equipmentBoxId(draft) === null
                ? 'Nearest first.'
                : 'It stands at the box’s pin, and moves with it.'
            }
            onChange={(boxId) => {
              form.replace(withBox(draft, boxId))
            }}
          />
        ) : null}
        <TextField
          label="Notes"
          id="equipment-notes"
          value={draft.notes}
          multiline
          onChange={(notes) => {
            form.update({ notes })
          }}
        />
      </fieldset>
      {isNew || record.kind !== 'VALVE_BOX' ? null : (
        <ValveBoxContents box={record} context={context} />
      )}
      {isNew ? null : (
        <RecordMedia
          owner={{ irrigationEquipmentId: record.id }}
          pendingKey={`equipment:${record.id}`}
          context={context}
        />
      )}
      {isNew ? null : (
        <RecordHistory
          siteId={context.site.siteId}
          entity="EQUIPMENT"
          recordId={record.id}
        />
      )}
      <EditorFooter
        locked={context.canEditRecord === false}
        askWhen={context.asksWhen === true}
        isNew={isNew}
        dirty={form.dirty}
        error={form.error}
        saved={form.savedAt !== null}
        onSave={(isCorrection, changedOn) => {
          if (form.save(isCorrection, changedOn) && isNew)
            context.open({
              entity: 'equipment',
              record: form.draft,
              isNew: false,
            })
        }}
        question={form.saveQuestion}
        onCancel={context.close}
        mapAction={moveAction(context)}
        remove={{
          confirming: form.confirmingRemove,
          setConfirming: form.setConfirmingRemove,
          onRemove: form.remove,
          question: form.removeQuestion,
          noun: IRRIGATION_EQUIPMENT_KIND_LABELS[kind].toLowerCase(),
        }}
      />
      {isNew || form.dirty ? null : (
        <NextStep anchor={equipmentAnchor(record, site)} context={context} />
      )}
      {isNew ? null : (
        <ProblemsOn
          subject={{ kind: 'equipment', equipmentId: record.id }}
          problems={context.site.problems.filter(
            (problem) => problemEquipmentId(problem) === record.id,
          )}
          context={context}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Problem                                                                    */
/* -------------------------------------------------------------------------- */

function ProblemEditor({
  record,
  isNew,
  context,
}: {
  record: IrrigationProblemDto
  isNew: boolean
  context: EditorContext
}) {
  const form = useRecordDraft('problem', record, isNew, context, (next) => ({
    kind: 'upsert',
    entity: 'problem',
    record: next,
  }))
  const { draft } = form
  const [picking, setPicking] = React.useState(false)
  const subject = problemSubject(draft, context.site)
  const typeOptions = context.problemTypes
    .filter(
      (type) =>
        type.id === draft.typeId ||
        (type.archivedAt === null && type.appliesTo.includes(subject)),
    )
    .map((type) => ({ value: type.id, label: type.name }))
  // The directory's clip of what this problem looks like, if it has one.
  const explainerId =
    context.problemTypes.find((type) => type.id === draft.typeId)?.videoId ??
    null
  return (
    // A problem is a visit's: one shown from a visit that cannot take
    // findings now is read, not edited (see `canRecordIn`).
    <fieldset
      disabled={!context.canRecordFindings}
      className="m-0 min-w-0 space-y-4 border-0 p-0 disabled:opacity-60"
    >
      <p className="text-sm text-secondary-text">
        {IRRIGATION_PROBLEM_SUBJECT_LABELS[subject]}
      </p>
      <SelectField
        label="Problem"
        id="problem-type"
        value={draft.typeId}
        options={typeOptions}
        emptyLabel="Other (describe it below)"
        onChange={(typeId) => {
          const type = context.problemTypes.find((entry) => entry.id === typeId)
          form.update(withUsualRepair({ ...draft, typeId }, type))
        }}
      />
      {explainerId === null ? null : <ProblemExplainer videoId={explainerId} />}
      <Stepper
        label="How many"
        value={draft.count}
        min={1}
        onChange={(count) => {
          form.update({ count })
        }}
      />
      <Chips
        label="Severity"
        value={draft.severity}
        noneLabel="Not rated"
        options={PROBLEM_SEVERITIES.map((value) => ({
          value,
          label: String(value),
        }))}
        onChange={(severity) => {
          form.update({ severity })
        }}
      />
      <TextField
        label={draft.typeId === null ? 'What it is' : 'Notes'}
        id="problem-notes"
        value={draft.notes}
        multiline
        onChange={(notes) => {
          form.update({ notes })
        }}
      />
      <Section title="Repair">
        <TextField
          label="Labor (minutes)"
          id="problem-labor"
          value={
            draft.laborMinutes === null ? null : String(draft.laborMinutes)
          }
          inputMode="numeric"
          onChange={(value) => {
            const minutes = Number(value)
            form.update({
              laborMinutes:
                value === null || !Number.isInteger(minutes) ? null : minutes,
            })
          }}
        />
        {draft.materials.length === 0 ? null : (
          <ul className="divide-y divide-default rounded-md border border-default">
            {draft.materials.map((item) => (
              <li
                key={item.material.uuid}
                className="flex items-center gap-2 px-2 py-1.5"
              >
                <input
                  className="h-9 w-16 rounded-md border border-default bg-surface px-2 text-sm"
                  inputMode="decimal"
                  aria-label={`Quantity of ${item.material.name}`}
                  value={item.quantity}
                  onChange={(event) => {
                    form.update({
                      materials: draft.materials.map((entry) =>
                        entry.material.uuid === item.material.uuid
                          ? { ...entry, quantity: event.target.value }
                          : entry,
                      ),
                    })
                  }}
                />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {item.material.name}
                </span>
                <button
                  type="button"
                  className="rounded p-1 text-muted-text hover:bg-subtle"
                  aria-label={`Remove ${item.material.name}`}
                  onClick={() => {
                    form.update({
                      materials: draft.materials.filter(
                        (entry) => entry.material.uuid !== item.material.uuid,
                      ),
                    })
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {picking ? (
          <PartPicker
            autoFocus
            excludeUuids={draft.materials.map((item) => item.material.uuid)}
            onPick={(part) => {
              form.update({
                materials: [
                  ...draft.materials,
                  { material: toSummary(part), quantity: '1' },
                ],
              })
              setPicking(false)
            }}
          />
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setPicking(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add a part
          </Button>
        )}
      </Section>
      {isNew ? null : (
        <RecordMedia
          owner={{ irrigationProblemId: record.id }}
          pendingKey={`problem:${record.id}`}
          context={context}
          label="Photos of this problem"
        />
      )}
      <EditorFooter
        askWhen={context.asksWhen === true}
        isNew={isNew}
        dirty={form.dirty}
        error={form.error}
        saved={form.savedAt !== null}
        onSave={(isCorrection, changedOn) => {
          if (form.save(isCorrection, changedOn) && isNew)
            context.open({
              entity: 'problem',
              record: form.draft,
              isNew: false,
            })
        }}
        question={form.saveQuestion}
        onCancel={context.close}
        mapAction={moveAction(context)}
        remove={{
          confirming: form.confirmingRemove,
          setConfirming: form.setConfirmingRemove,
          onRemove: form.remove,
          question: form.removeQuestion,
          noun: 'problem',
        }}
      />
    </fieldset>
  )
}

/* -------------------------------------------------------------------------- */
/* Dispatch                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The sheet one level up from this one, for the link back: the source a
 * system component or timer is on, the timer a zone list belongs to, the
 * zone list a zone is in, what a problem is on. Null at the top.
 */
export function parentOf(
  target: SheetTarget,
  site: IrrigationSiteDto,
): SheetTarget | null {
  function source(id: string | null): SheetTarget | null {
    const record = site.waterSources.find((item) => item.id === id)
    return record === undefined
      ? null
      : { entity: 'waterSource', record, isNew: false }
  }
  function timer(id: string | null): SheetTarget | null {
    const record = site.timers.find((item) => item.id === id)
    return record === undefined
      ? null
      : { entity: 'timer', record, isNew: false }
  }
  function zone(id: string | null): SheetTarget | null {
    const record = site.zones.find((item) => item.id === id)
    return record === undefined
      ? null
      : { entity: 'zone', record, isNew: false }
  }
  function equipment(id: string | null): SheetTarget | null {
    const record = site.equipment.find((item) => item.id === id)
    return record === undefined
      ? null
      : { entity: 'equipment', record, isNew: false }
  }
  switch (target.entity) {
    case 'waterSource':
      return null
    case 'timer':
      return source(target.record.waterSourceId)
    case 'zones':
      return timer(target.record.id)
    case 'zone': {
      const record = site.timers.find(
        (item) => item.id === target.record.timerId,
      )
      return record === undefined
        ? null
        : { entity: 'zones', record, isNew: false }
    }
    case 'equipment': {
      const { record } = target
      const box = equipment(equipmentBoxId(record))
      if (box !== null) return box
      if (record.kind === 'VALVE') return zone(record.zoneId)
      if (record.kind === 'SENSOR') return timer(record.timerId)
      if (
        record.kind === 'MASTER_VALVE' ||
        record.kind === 'BACKFLOW' ||
        record.kind === 'BALL_VALVE' ||
        record.kind === 'WELL_COMPONENT'
      ) {
        return source(record.waterSourceId)
      }
      return null
    }
    case 'problem':
      return (
        zone(problemZoneId(target.record)) ??
        timer(problemTimerId(target.record)) ??
        equipment(problemEquipmentId(target.record)) ??
        source(problemWaterSourceId(target.record))
      )
  }
}

/** What a sheet is titled. */
export function sheetTitle(
  target: SheetTarget,
  context: Pick<EditorContext, 'site' | 'problemTypes'>,
): string {
  const { site } = context
  const prefix = target.isNew ? 'New ' : ''
  switch (target.entity) {
    case 'waterSource':
      return target.isNew
        ? 'New water source'
        : waterSourceName(target.record.kind, target.record.label)
    case 'timer':
      return target.isNew ? 'New timer' : timerName(target.record, site.timers)
    case 'zones':
      return `${timerName(target.record, site.timers)} zones`
    case 'zone':
      return `${prefix}${zoneName(target.record, site.timers)}`
    case 'equipment':
      return target.isNew
        ? `New ${IRRIGATION_EQUIPMENT_KIND_LABELS[target.record.kind].toLowerCase()}`
        : equipmentName(target.record, site)
    case 'problem':
      return target.isNew
        ? 'New problem'
        : problemTitle(target.record, context.problemTypes)
  }
}

/**
 * The editor for whatever a sheet is showing. Keyed by record and newness by
 * the caller, so switching records resets the draft rather than carrying one
 * record's half-typed edit onto another.
 */
export function RecordEditor({
  target,
  context,
}: {
  target: SheetTarget
  context: EditorContext
}) {
  switch (target.entity) {
    case 'waterSource':
      return (
        <WaterSourceEditor
          record={target.record}
          isNew={target.isNew}
          context={context}
        />
      )
    case 'timer':
      return (
        <TimerEditor
          record={target.record}
          isNew={target.isNew}
          context={context}
        />
      )
    case 'zones':
      return <ZoneListEditor timer={target.record} context={context} />
    case 'zone':
      return (
        <ZoneEditor
          record={target.record}
          isNew={target.isNew}
          showLastVisit={target.showLastVisit === true}
          context={context}
        />
      )
    case 'equipment':
      return (
        <EquipmentEditor
          record={target.record}
          isNew={target.isNew}
          context={context}
        />
      )
    case 'problem':
      return (
        <ProblemEditor
          record={target.record}
          isNew={target.isNew}
          context={context}
        />
      )
  }
}
